import React, { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CertCatalogLookup, LookupItem } from '../types';
import { useCrewLookups } from '../useData';
import {
  RankRequirementsByNationalityReport,
  type RankReqByNatMatrixRow,
  type RankReqRankColumn,
} from './RankRequirementsByNationalityReport';
import {
  rankRequirementsBackPath,
  rankRequirementsVesselApiUrl,
} from './rankRequirementsPrintMeta';

type RankRequirementApiRow = {
  id: number;
  rankId: number;
  vesselId?: number | null;
  certCatalogId: number;
  nationalityId: number;
  isMandatory: boolean;
  notes?: string | null;
  sortOrder: number;
  isActive: boolean;
  rank?: { id: number; name: string; abrv?: string | null; sortOrder?: number | null } | null;
  vessel?: { id: number; vesselName: string } | null;
  certCatalog?: { id: number; name: string; categoryId?: number | null } | null;
  nationality?: { id: number; name: string; code?: string | null } | null;
};

const RankRequirementsByNationalityReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const { lookups } = useCrewLookups();

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const nationalityIdRaw = searchParams.get('nationalityId')?.trim() ?? '';
  const nationalityId = Number(nationalityIdRaw);
  const listUrl = useMemo(() => rankRequirementsVesselApiUrl(searchParams), [searchParams]);

  const { data, error, isLoading } = useSWR<RankRequirementApiRow[]>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const ranksLookup = lookups?.ranks ?? [];
  const vessels = lookups?.vessels ?? [];
  const certCatalogs = lookups?.certCatalogs ?? [];
  const certCategories = lookups?.certCategories ?? [];
  const allNationalities = lookups?.nationalities ?? [];

  const vesselName = useMemo(() => {
    const fromApi = data?.find((r) => r.vessel?.vesselName)?.vessel?.vesselName;
    if (fromApi) return fromApi;
    return vessels.find((v) => String(v.id) === vesselId)?.vesselName ?? `Vessel #${vesselId}`;
  }, [data, vesselId, vessels]);

  const nationality = useMemo(() => {
    const fromApi = data?.find((r) => r.nationalityId === nationalityId)?.nationality;
    if (fromApi?.name) return fromApi;
    return allNationalities.find((n) => n.id === nationalityId) ?? null;
  }, [allNationalities, data, nationalityId]);

  const categoryById = useMemo(() => {
    const map = new Map<number, LookupItem>();
    for (const c of certCategories) map.set(c.id, c);
    return map;
  }, [certCategories]);

  const certById = useMemo(() => {
    const map = new Map<number, CertCatalogLookup>();
    for (const c of certCatalogs) map.set(c.id, c);
    return map;
  }, [certCatalogs]);

  const rankMetaById = useMemo(() => {
    const map = new Map<number, LookupItem>();
    for (const r of ranksLookup) map.set(r.id, r);
    return map;
  }, [ranksLookup]);

  const { rows, ranks } = useMemo(() => {
    const mandatory = (data ?? []).filter(
      (r) => r.isMandatory && r.nationalityId === nationalityId,
    );

    const rankIds = new Set<number>();
    for (const r of mandatory) rankIds.add(r.rankId);

    const rankCols: RankReqRankColumn[] = Array.from(rankIds)
      .map((id) => {
        const fromApi = mandatory.find((r) => r.rankId === id)?.rank;
        const fromLookup = rankMetaById.get(id);
        const abrv = fromApi?.abrv || fromLookup?.abrv;
        const name = fromApi?.name || fromLookup?.name || `#${id}`;
        const sortOrder =
          fromApi?.sortOrder ?? fromLookup?.sortOrder ?? 999_999;
        return {
          id,
          label: (abrv || name).trim() || name,
          sortOrder: Number(sortOrder) || 999_999,
          name,
        };
      })
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.name.localeCompare(b.name);
      })
      .map(({ id, label }) => ({ id, label }));

    const orderOf = (n: number | null | undefined, fallback = 999_999) => {
      const v = Number(n);
      return Number.isFinite(v) ? v : fallback;
    };

    const byCert = new Map<number, RankReqByNatMatrixRow>();

    for (const r of mandatory) {
      const cert = certById.get(r.certCatalogId);
      const categoryId = cert?.categoryId ?? r.certCatalog?.categoryId ?? null;
      const catLookup = categoryId != null ? categoryById.get(categoryId) : undefined;
      const category = catLookup?.name?.trim() || 'Uncategorized';
      const certificate = r.certCatalog?.name || cert?.name || `Cert #${r.certCatalogId}`;
      const existing = byCert.get(r.certCatalogId);
      if (existing) {
        existing.byRank[r.rankId] = true;
      } else {
        byCert.set(r.certCatalogId, {
          key: String(r.certCatalogId),
          category,
          certificate,
          categoryOrder: orderOf(catLookup?.sortOrder, categoryId == null ? 999_999 : 0),
          certOrder: orderOf(cert?.sortOrder),
          byRank: { [r.rankId]: true },
        });
      }
    }

    const certRows = Array.from(byCert.values()).sort((a, b) => {
      if (a.categoryOrder !== b.categoryOrder) return a.categoryOrder - b.categoryOrder;
      if (a.certOrder !== b.certOrder) return a.certOrder - b.certOrder;
      return a.certificate.localeCompare(b.certificate, undefined, { sensitivity: 'base' });
    });

    return { rows: certRows, ranks: rankCols };
  }, [categoryById, certById, data, nationalityId, rankMetaById]);

  const backTo = rankRequirementsBackPath(searchParams);
  const nationalityLabel =
    nationality?.code || nationality?.name || `Nationality #${nationalityIdRaw}`;

  if (!vesselId || !nationalityIdRaw || !Number.isFinite(nationalityId)) {
    return <Navigate to="/parameters/crew?section=certificate-requirements" replace />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Document Requirements…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load document requirements.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Document Requirements
        </Link>
      </div>
    );
  }

  const subtitle = `${vesselName} · ${nationalityLabel}`;

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Document Requirements"
      title="Document Requirements"
      subtitle={subtitle}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <RankRequirementsByNationalityReport
        rows={rows}
        ranks={ranks}
        vesselName={vesselName}
        nationalityLabel={nationalityLabel}
      />
    </ReportView>
  );
};

export default RankRequirementsByNationalityReportPage;
